import type { Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { LASER } from './warden-parts';

/**
 * The Warden's rotating laser sweep (core + rage): emitter charge glow →
 * thick beam that sweeps past the ship. Pure state + a draw call; the boss
 * owns the Graphics and the hit test result.
 */
export class WardenLaser {
  private timer = LASER.interval * 0.6;
  private charge = 0;
  private active = 0;
  private angle = 0;
  /** Ship inside the beam this step (contact damage) */
  playerInBeam = false;

  /** Charging or firing — the core hatch seals for it (docs/02 B1). */
  get busy(): boolean {
    return this.charge > 0 || this.active > 0;
  }

  /** @returns true on the step the sweep ends */
  update(dt: number, x: number, y: number, px: number, py: number, tempo: number): boolean {
    this.playerInBeam = false;
    if (this.active > 0) {
      this.active -= dt;
      this.angle += LASER.sweepSpeed * dt;
      // Point-to-ray distance test
      const dx = px - x;
      const dy = py - y;
      const along = dx * Math.cos(this.angle) + dy * Math.sin(this.angle);
      if (along > 0 && along < LASER.length) {
        const perp = Math.abs(-dx * Math.sin(this.angle) + dy * Math.cos(this.angle));
        if (perp < LASER.width / 2 + LASER.hitPad) this.playerInBeam = true;
      }
      return this.active <= 0;
    }
    if (this.charge > 0) {
      this.charge -= dt;
      if (this.charge <= 0) this.active = LASER.active;
      return false;
    }
    this.timer -= dt * tempo;
    if (this.timer <= 0) {
      this.timer = LASER.interval;
      this.charge = LASER.charge;
      this.angle = Math.atan2(py - y, px - x) - LASER.lead;
    }
    return false;
  }

  /** Draw in the boss view (origin = emitter). */
  render(g: Graphics): void {
    g.clear();
    if (this.charge > 0) {
      const p = 1 - this.charge / LASER.charge;
      g.circle(0, 0, 16 + p * 26).fill({ color: LASER.glow, alpha: 0.25 + p * 0.4 });
      // A faint heat shimmer where the beam will start — not a full red line
      const len = LASER.previewLen * p;
      g.moveTo(0, 0)
        .lineTo(Math.cos(this.angle) * len, Math.sin(this.angle) * len)
        .stroke({ color: LASER.glow, width: 6, alpha: 0.2 + p * 0.3, cap: 'round' });
    } else if (this.active > 0) {
      const ex = Math.cos(this.angle) * LASER.length;
      const ey = Math.sin(this.angle) * LASER.length;
      g.moveTo(0, 0).lineTo(ex, ey)
        .stroke({ color: COLORS.dangerOrange, width: LASER.width, alpha: 0.85 });
      g.moveTo(0, 0).lineTo(ex, ey)
        .stroke({ color: LASER.core, width: LASER.width * 0.4, alpha: 0.95 });
      g.circle(0, 0, 26).fill({ color: LASER.core, alpha: 0.9 });
    }
  }
}
